// LeadDetailView.tsx
// Restyled to match the Leads.tsx list screen: SHELL/INNER card shells,
// shared G/C design tokens, lucide-react icons instead of PrimeIcons.
// All existing functionality preserved: notes, call logging, timeline,
// QR widget, assignment panel, edit modal integration.
// Call log dialog extracted to LogCallModal.
// QR widgets combined into a single Quick Share card (QR + WhatsApp/Call/View Lead
// buttons), placed above Quick Actions in the right column.

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import QRCode from 'qrcode';
import AddEditLeadModal, { SavedLead } from './parts/AddEditLeadModal';
import LogCallModal, { CallLogLead } from './parts/LogCallModal';
import LeadTimelineSidebar from './parts/LeadTimelineSidebar';
import StatusChangeModal from './parts/StatusChangeModal';
import LeadOriginCard from './parts/LeadOriginCard';
import { LeadOrigin } from './parts/leadOrigin';
import { EXIT_STATUSES, MAIN_FLOW, NEXT_STEPS, STATUS_EMOJI, getStatusStyle, statusLabel, toValidStatus } from './parts/leadStatuses';
import { G, C } from '../../components/GlobalComponents/uiTokens';
import { GlobalTextArea } from '../../components/GlobalComponents/GlobalTextArea';
import { GlobalDateField } from '../../components/GlobalComponents/GlobalDateField';
import {
  AlignLeft,
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Camera,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  ExternalLink,
  Globe,
  IdCard,
  List,
  Lock,
  Loader2,
  Mail,
  MapPin,
  MessageCircle,
  MessageSquare,
  Pencil,
  Phone,
  PlusCircle,
  QrCode,
  RotateCcw,
  Save,
  Search,
  Send,
  Share2,
  Tag,
  User,
  UserPlus,
  Users,
  X,
  Zap,
} from 'lucide-react';

// ── TYPES ─────────────────────────────────────────────────────────────────────
interface ConversionRequest {
  status: 'pending' | 'approved' | 'rejected' | string;
  note?: string | null;
  review_note?: string | null;
  auto_approved?: boolean;
}
interface AssignedTo { id: string; email: string; role?: string | null; agency_id?: string | null; }
interface AssignedBy { id: string; email: string; role?: string | null; }
export interface Note {
  text: string; type: 'note' | 'call_log'; outcome: string | null;
  extra_note: string | null; event_date: string | null;
  created_at: string | null; added_by_email: string | null; added_by_role: string | null;
}
export interface Lead {
  id: string; name: string; phone: string | null; email: string | null;
  source: string | null; status: string | null; notes: string | null;
  notes_list: Note[]; city: string | null; interested_in: string | null; conversion_request?: ConversionRequest | null; created_at: string | null;
  assigned_to: AssignedTo | null; assigned_by: AssignedBy | null; assigned_at: string | null;
  follow_up_at?: string | null; // next follow-up day (IST); cleared by the next call log
  sheet_id?: string | null; sheet_import_id?: string | null;
  origin?: LeadOrigin | null; custom_fields?: Record<string, string> | null;
}
export type TimelineEvent =
  | { kind: 'created'; date: string }
  | { kind: 'note'; date: string; note: Note }
  | { kind: 'call_log'; date: string; note: Note };

// ── SHELL / INNER wrappers (matches Leads.tsx card chrome) ────────────────────
export const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
export const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};
const LABEL: React.CSSProperties = {
  fontSize: '0.6rem',
  fontWeight: 600,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: C.sub,
};

export type IconCmp = React.ComponentType<{ size?: number; style?: React.CSSProperties; color?: string }>;
export const SOURCE_ICONS: Record<string, IconCmp> = {
  facebook: Share2,
  instagram: Camera,
  google: Search,
  referral: Users,
  website: Globe,
  walk_in: MapPin,
  whatsapp: MessageCircle,
};

const OUTCOME_COLORS: Record<string, { bg: string; color: string; dot: string; border: string }> = {
  Answered: { bg: '#e8f8e8', color: '#2e7d32', dot: '#2e7d32', border: 'rgba(46,125,50,0.25)' },
  'Not Answered': { bg: '#fff3e0', color: '#e65100', dot: '#e65100', border: 'rgba(230,81,0,0.25)' },
  'Not Attempted': { bg: '#f3e5f5', color: '#7b1fa2', dot: '#7b1fa2', border: 'rgba(123,31,162,0.25)' },
  'Call Back Later': { bg: '#e3f2fd', color: '#1565c0', dot: '#1565c0', border: 'rgba(21,101,192,0.25)' },
  'Not Interested': { bg: '#fce4ec', color: '#c62828', dot: '#c62828', border: 'rgba(198,40,40,0.25)' },
  Interested: { bg: '#e8f5e9', color: '#1b5e20', dot: '#1b5e20', border: 'rgba(27,94,32,0.25)' },
  'Left Voicemail': { bg: '#f3f4f6', color: '#374151', dot: '#374151', border: 'rgba(55,65,81,0.2)' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (d: string | null) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
};
/** "Today", "Overdue · 3 Oct", "8 Oct" for a follow-up day (IST). */
const followUpInfo = (iso?: string | null): { text: string; color: string; bg: string } | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  const ist = (ms: number) => new Date(ms + 330 * 60000).toISOString().slice(0, 10);
  const day = ist(t);
  const today = ist(Date.now());
  const nice = new Date(day + 'T00:00:00Z').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  if (day === today) return { text: 'Today', color: '#1d4ed8', bg: 'rgba(29,78,216,0.08)' };
  if (day < today) return { text: `Overdue · ${nice}`, color: '#c62828', bg: 'rgba(239,68,68,0.08)' };
  return { text: nice, color: '#4a6865', bg: 'rgba(17,85,80,0.07)' };
};
const AVCOLORS = ['#115550', '#16706a', '#1a8a82', '#2563eb', '#7c3aed', '#db2777', '#ea580c'];
export const avc = (s: string | null) => {
  if (!s) return AVCOLORS[0];
  let h = 0;
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  return AVCOLORS[Math.abs(h) % AVCOLORS.length];
};
const ini2 = (e: string | null) => (e ? e.split('@')[0].substring(0, 2).toUpperCase() : '?');
export const getInitials = (n: string) => n.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();
export const getBadgeStyle = (s: string | null) => getStatusStyle(s);

// The API omits notes_list for leads with no notes yet — always normalise it.
const withNotes = (l: any): Lead => ({ ...l, notes_list: Array.isArray(l?.notes_list) ? l.notes_list : [] });
const getOutcomeStyle = (o: string | null) =>
  OUTCOME_COLORS[o ?? ''] ?? { bg: '#f3f7f6', color: '#4a6865', dot: C.teal, border: 'rgba(22,112,106,0.2)' };

// ── QR CODE ───────────────────────────────────────────────────────────────────
const QRCodeSVG: React.FC<{ value: string; size?: number; fgColor?: string }> = ({ value, size = 110, fgColor = C.teal }) => {
  const [svgString, setSvgString] = useState<string>('');

  useEffect(() => {
    if (!value || value === 'N/A') { setSvgString(''); return; }
    QRCode.toString(value, { type: 'svg', width: size, color: { dark: fgColor, light: '#ffffff' }, errorCorrectionLevel: 'H', margin: 2 })
      .then((svg) => setSvgString(svg))
      .catch((err) => { console.error('QR generation failed:', err); setSvgString(''); });
  }, [value, size, fgColor]);

  if (!svgString) {
    return (
      <div style={{ width: size, height: size, background: '#f8f9f8', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${G.line}`, fontSize: '0.65rem', color: C.muted, fontWeight: 600 }}>
        QR
      </div>
    );
  }
  return <div dangerouslySetInnerHTML={{ __html: svgString }} style={{ display: 'block', width: size, height: size }} />;
};

// ── QUICK SHARE CARD ──────────────────────────────────────────────────────────
// Combined QR display: clicking WhatsApp / Call / View Lead reveals the QR
// code specific to that action (not the action itself).
type ShareType = 'whatsapp' | 'call' | 'lead';
export const QuickShareCard: React.FC<{ lead: Lead }> = ({ lead }) => {
  const [activeType, setActiveType] = useState<ShareType | null>(null);
  const phoneClean = lead.phone ? lead.phone.replace(/[\s\-\(\)]/g, '') : '';

  const QR_DATA: Record<ShareType, string> = {
    whatsapp: phoneClean ? `https://wa.me/${phoneClean}` : 'N/A',
    call: phoneClean ? `tel:${phoneClean}` : 'N/A',
    lead: `${window.location.origin}/leads/${lead.id}`,
  };
  const QR_LABELS: Record<ShareType, string> = {
    whatsapp: 'WhatsApp',
    call: 'Call',
    lead: 'View Lead',
  };

  const revealed = activeType !== null;
  const toggle = (type: ShareType) => setActiveType((cur) => (cur === type ? null : type));

  const BUTTONS: { type: ShareType; label: string; icon: IconCmp; bg: string; col: string; brd: string; shadow: string; disabled?: boolean }[] = [
    { type: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, bg: '#25D366', col: '#fff', brd: 'transparent', shadow: '0 2px 8px rgba(37,211,102,0.3)', disabled: !phoneClean },
    { type: 'call', label: 'Call', icon: Phone, bg: C.teal, col: '#fff', brd: 'transparent', shadow: '0 2px 8px rgba(17,85,80,0.3)', disabled: !phoneClean },
    { type: 'lead', label: 'View Lead', icon: ExternalLink, bg: C.limeDim, col: '#4a5e00', brd: C.limeBorder, shadow: 'none' },
  ];

  return (
    <div style={SHELL}>
      <div style={{ ...INNER, overflow: 'hidden' }}>
        <div style={{ padding: '9.2px 13px', background: 'rgba(17,85,80,0.07)', borderBottom: `1px solid ${G.line}`, display: 'flex', alignItems: 'center', gap: '7.4px' }}>
          <div style={{ width: '3px', height: '14.8px', background: `linear-gradient(180deg,${C.lime},#8aaa00)`, borderRadius: '3px' }} />
          <QrCode size={13} color={C.teal} />
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: C.teal }}>{revealed ? `${QR_LABELS[activeType!]} QR` : 'Quick Share'}</span>
        </div>

        <div style={{ padding: '16.6px 16.6px 11px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '9.2px' }}>
          <div style={{ position: 'relative', width: 130, height: 130, borderRadius: '9.2px', overflow: 'hidden', border: `1px solid ${G.line}`, flexShrink: 0 }}>
            {revealed ? (
              <QRCodeSVG value={QR_DATA[activeType!]} size={130} fgColor={C.teal} />
            ) : (
              <div style={{ width: '100%', height: '100%', background: '#f8f9f8', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4.6px' }}>
                <Lock size={20} color={C.muted} />
                <span style={{ fontSize: '0.58rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.6px', textAlign: 'center', padding: '0 9px' }}>
                  Select an option below
                </span>
              </div>
            )}
          </div>
          {revealed && (
            <span style={{ fontSize: '0.65rem', fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>
              Scan for {QR_LABELS[activeType!]}
            </span>
          )}
        </div>

        <div style={{ padding: '0 13px 13px', display: 'flex', gap: '6.5px' }}>
          {BUTTONS.map((btn) => {
            const isActive = activeType === btn.type;
            return (
              <button
                key={btn.type}
                onClick={() => toggle(btn.type)}
                disabled={btn.disabled}
                style={{
                  flex: 1,
                  height: '32px',
                  borderRadius: '7.4px',
                  border: isActive ? `2px solid ${btn.col === '#fff' ? '#fff' : '#4a5e00'}` : `1px solid ${btn.brd}`,
                  background: btn.bg,
                  color: btn.col,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  cursor: btn.disabled ? 'not-allowed' : 'pointer',
                  opacity: btn.disabled ? 0.5 : isActive ? 1 : 0.85,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '3.7px',
                  boxShadow: isActive ? btn.shadow : 'none',
                  transition: 'all 0.18s',
                }}
              >
                <btn.icon size={12} /> {btn.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── SECTION HEADER ─────────────────────────────────────────────────────────────
export const SectionHeader: React.FC<{ icon: IconCmp; title: string; count?: number; right?: React.ReactNode }> = ({ icon: Icon, title, count, right }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14.8px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}>
      <div style={{ width: '3px', height: '14.8px', background: `linear-gradient(180deg,${C.lime},#8aaa00)`, borderRadius: '3px' }} />
      <div style={{ width: '27px', height: '27px', background: C.lime, borderRadius: '7.4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={13} color={C.teal} />
      </div>
      <span style={{ fontSize: '0.81rem', fontWeight: 700, color: C.teal }}>{title}</span>
      {count !== undefined && (
        <span style={{ padding: '1.8px 6.5px', borderRadius: '20px', background: 'rgba(17,85,80,0.07)', color: C.tealMid, fontSize: '0.64rem', fontWeight: 600 }}>{count}</span>
      )}
    </div>
    {right}
  </div>
);

// ── CARD (SHELL/INNER wrapped section, matches Leads.tsx table/header cards) ──
export const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={SHELL}>
    <div style={{ ...INNER, padding: '16.6px 18.5px', ...style }}>{children}</div>
  </div>
);

// ── INFO ROW ──────────────────────────────────────────────────────────────────
export const InfoRow: React.FC<{ icon: IconCmp; label: string; value: string | null }> = ({ icon: Icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px', padding: '8.3px 0', borderBottom: `1px solid ${G.softer}` }}>
    <div style={{ width: '26px', height: '26px', background: C.limeDim, borderRadius: '6.5px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={12} color={C.teal} />
    </div>
    <span style={{ fontSize: '0.67rem', color: C.muted, fontWeight: 600, width: '65px', flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</span>
    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1f2937', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value || '—'}</span>
  </div>
);

// ── NOTE ITEM ─────────────────────────────────────────────────────────────────
const NoteItem: React.FC<{ note: Note }> = ({ note }) => {
  const isCallLog = note.type === 'call_log';
  const os = getOutcomeStyle(note.outcome);
  return (
    <div style={{ marginBottom: '9.2px', padding: '11px 13px', background: G.shellBg, borderRadius: '9.2px', border: `1px solid ${isCallLog ? os.border : G.line}`, borderLeft: `3px solid ${isCallLog ? os.dot : C.lime}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '7.4px', marginBottom: '5.5px' }}>
        <span style={{ padding: '1.8px 7.4px', borderRadius: '20px', fontSize: '0.63rem', fontWeight: 700, background: isCallLog ? os.bg : C.limeDim, color: isCallLog ? os.color : '#4a5e00', border: `1px solid ${isCallLog ? os.border : C.limeBorder}` }}>
          {isCallLog ? `📞 ${note.outcome || 'Call Log'}` : '📝 Note'}
        </span>
        <span style={{ fontSize: '0.65rem', color: C.muted, whiteSpace: 'nowrap' }}>{fmtDate(note.created_at)}</span>
      </div>
      <p style={{ margin: '0 0 5.5px 0', fontSize: '0.76rem', color: '#1f2937', lineHeight: 1.55 }}>
        {isCallLog ? note.extra_note || '—' : note.text}
      </p>
      {note.added_by_email && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4.6px' }}>
          <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: avc(note.added_by_email), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.5rem', fontWeight: 700 }}>
            {ini2(note.added_by_email)}
          </div>
          <span style={{ fontSize: '0.65rem', color: C.muted }}>{note.added_by_email.split('@')[0]}</span>
        </div>
      )}
    </div>
  );
};

// ── TIMELINE ITEM ─────────────────────────────────────────────────────────────
export const TimelineItem: React.FC<{ ev: TimelineEvent; isLast: boolean; lead: Lead }> = ({ ev, isLast, lead }) => {
  const os = ev.kind === 'call_log' ? getOutcomeStyle(ev.note.outcome) : null;
  const dotColor = ev.kind === 'created' ? C.lime : ev.kind === 'call_log' ? os?.dot || C.tealMid : C.tealMid;
  return (
    <div style={{ display: 'flex', gap: '13px', marginBottom: '3.7px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '26px', flexShrink: 0 }}>
        <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: dotColor, border: `2px solid ${dotColor}`, marginTop: '3.7px', flexShrink: 0, boxShadow: `0 0 0 3px ${dotColor}22` }} />
        {!isLast && <div style={{ width: '2px', flex: 1, background: `linear-gradient(${G.line}, transparent)`, minHeight: '22px', marginTop: '2.8px', marginBottom: '-3.7px' }} />}
      </div>
      <div style={{ flex: 1, paddingBottom: isLast ? 0 : '16.6px' }}>
        {ev.kind === 'created' && (
          <div style={{ padding: '9.2px 12px', background: C.limeDim, border: `1px solid ${C.limeBorder}`, borderRadius: '8.3px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3.7px' }}>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#4a5e00', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🎉 Lead Created</span>
              <span style={{ fontSize: '0.65rem', color: C.muted }}>{fmtDate(ev.date)}</span>
            </div>
            {lead.notes && <p style={{ margin: 0, fontSize: '0.75rem', color: '#1f2937', lineHeight: 1.5 }}>{lead.notes}</p>}
          </div>
        )}
        {ev.kind === 'call_log' && (
          <div style={{ padding: '9.2px 12px', background: os?.bg || G.shellBg, border: `1px solid ${os?.border || G.line}`, borderRadius: '8.3px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3.7px' }}>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: os?.color || C.teal, textTransform: 'uppercase', letterSpacing: '0.5px' }}>📞 {ev.note.outcome || 'Call Log'}</span>
              <span style={{ fontSize: '0.65rem', color: C.muted }}>{fmtDate(ev.note.event_date || ev.note.created_at)}</span>
            </div>
            {ev.note.extra_note && <p style={{ margin: '0 0 3.7px', fontSize: '0.75rem', color: '#1f2937', lineHeight: 1.5 }}>{ev.note.extra_note}</p>}
            {ev.note.added_by_email && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '3.7px', marginTop: '5.5px' }}>
                <div style={{ width: '15px', height: '15px', borderRadius: '50%', background: avc(ev.note.added_by_email), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.48rem', fontWeight: 700 }}>
                  {ini2(ev.note.added_by_email)}
                </div>
                <span style={{ fontSize: '0.63rem', color: C.muted }}>{ev.note.added_by_email.split('@')[0]}</span>
              </div>
            )}
          </div>
        )}
        {ev.kind === 'note' && (
          <div style={{ padding: '9.2px 12px', background: C.white, border: `1px solid ${G.line}`, borderLeft: `3px solid ${C.lime}`, borderRadius: '8.3px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3.7px' }}>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.5px' }}>📝 Note</span>
              <span style={{ fontSize: '0.65rem', color: C.muted }}>{fmtDate(ev.note.event_date || ev.note.created_at)}</span>
            </div>
            <p style={{ margin: '0 0 3.7px', fontSize: '0.75rem', color: '#1f2937', lineHeight: 1.5 }}>{ev.note.text}</p>
            {ev.note.added_by_email && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '3.7px', marginTop: '5.5px' }}>
                <div style={{ width: '15px', height: '15px', borderRadius: '50%', background: avc(ev.note.added_by_email), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.48rem', fontWeight: 700 }}>
                  {ini2(ev.note.added_by_email)}
                </div>
                <span style={{ fontSize: '0.63rem', color: C.muted }}>{ev.note.added_by_email.split('@')[0]}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
// ── STATUS PIPELINE + MOVE MENU ───────────────────────────────────────────────
// new → contacted → ┬ interested ┬→ qualified → converted
//                   └ follow_up  ┘
// • current stage pulses, the stages it can move to blink softly (clickable)
// • hovering an option in MOVE TO makes that stage + the connectors on the way
//   blink fast, so the user sees where the lead will land
// • converted shows amber "pending" while an admin has yet to approve it
const RANK: Record<string, number> = { new: 0, contacted: 1, interested: 2, follow_up: 2, qualified: 3, converted: 4 };
const NODE_ROW_H = 26;
const STACK_GAP = 8;
const STACK_H = NODE_ROW_H * 2 + STACK_GAP;
const BLINK_COLOR = '#8aaa00';

type NodeState = 'done' | 'current' | 'next' | 'pending' | 'upcoming';
type EdgeState = 'done' | 'next' | 'pending' | 'idle';

const edgeColor = (e: EdgeState, blink: boolean) =>
  blink ? BLINK_COLOR : e === 'done' ? C.teal : e === 'next' ? BLINK_COLOR : e === 'pending' ? '#d97706' : G.line;

const PipelineNode: React.FC<{ st: string; state: NodeState; dim: boolean; preview: boolean; onPick?: (st: string) => void }> = ({ st, state, dim, preview, onPick }) => {
  const clickable = state === 'next' && !!onPick;
  const cls =
    [state === 'current' ? 'ldv-node-current' : state === 'next' ? 'ldv-node-next' : state === 'pending' ? 'ldv-node-pending' : '', preview ? 'ldv-node-preview' : '']
      .filter(Boolean)
      .join(' ') || undefined;
  const circle = (
    <span
      className={cls}
      style={{
        width: '17px',
        height: '17px',
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxSizing: 'border-box',
        background: state === 'done' ? C.teal : state === 'current' ? C.lime : 'white',
        border: `1.5px ${state === 'next' || state === 'pending' ? 'dashed' : 'solid'} ${
          state === 'done' ? C.teal : state === 'current' ? C.teal : state === 'next' ? BLINK_COLOR : state === 'pending' ? '#d97706' : G.line
        }`,
        color: '#fff',
      }}
    >
      {state === 'done' ? (
        <Check size={10} strokeWidth={3.5} />
      ) : state === 'current' ? (
        <span className="ldv-node-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: C.teal, display: 'block' }} />
      ) : null}
    </span>
  );
  const label = (
    <span
      style={{
        fontSize: '0.64rem',
        fontWeight: state === 'current' ? 800 : state === 'next' || state === 'pending' ? 700 : 600,
        color: state === 'current' ? C.teal : state === 'next' ? '#4a5e00' : state === 'pending' ? '#b45309' : state === 'done' ? C.sub : C.muted,
        whiteSpace: 'nowrap',
      }}
    >
      {statusLabel(st)}
      {state === 'pending' ? ' · pending' : ''}
    </span>
  );
  const common: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: '5px', height: `${NODE_ROW_H}px`, opacity: dim ? 0.4 : 1 };
  if (clickable) {
    return (
      <button onClick={() => onPick!(st)} title={`Move to ${statusLabel(st)}`} style={{ ...common, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}>
        {circle}
        {label}
      </button>
    );
  }
  return (
    <div style={common}>
      {circle}
      {label}
    </div>
  );
};

const Line: React.FC<{ e: EdgeState; dim: boolean; blink: boolean }> = ({ e, dim, blink }) => {
  const color = edgeColor(e, blink);
  return (
    <span
      className={blink ? 'ldv-edge-blink' : undefined}
      style={{
        width: '16px',
        height: '2px',
        flexShrink: 0,
        opacity: dim ? 0.4 : 1,
        background: e === 'next' || e === 'pending' || blink ? `repeating-linear-gradient(90deg, ${color} 0 4px, transparent 4px 7px)` : color,
        borderRadius: '2px',
      }}
    />
  );
};

// Contacted ─┬─ Interested        and        Interested ─┬─ Qualified
//            └─ Follow up                    Follow up  ─┘
const Branch: React.FC<{ kind: 'fork' | 'merge'; e: EdgeState; dim: boolean; blink: boolean }> = ({ kind, e, dim, blink }) => {
  const c1 = NODE_ROW_H / 2;
  const c2 = NODE_ROW_H + STACK_GAP + NODE_ROW_H / 2;
  const mid = STACK_H / 2;
  const W = 22;
  const x = W / 2;
  const d =
    kind === 'fork'
      ? `M0 ${mid} H${x} M${x} ${c1} V${c2} M${x} ${c1} H${W} M${x} ${c2} H${W}`
      : `M0 ${c1} H${x} M0 ${c2} H${x} M${x} ${c1} V${c2} M${x} ${mid} H${W}`;
  return (
    <svg className={blink ? 'ldv-edge-blink' : undefined} width={W} height={STACK_H} style={{ flexShrink: 0, opacity: dim ? 0.4 : 1 }} aria-hidden>
      <path d={d} fill="none" stroke={edgeColor(e, blink)} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" strokeDasharray={e === 'next' || e === 'pending' || blink ? '3 3' : undefined} />
    </svg>
  );
};

const StatusPipeline: React.FC<{
  status: string | null;
  pendingConversion?: boolean;
  preview?: string | null;
  onPick?: (st: string) => void;
}> = ({ status, pendingConversion = false, preview = null, onPick }) => {
  const cur = toValidStatus(status);
  const curRank = RANK[cur];
  const exited = curRank === undefined;
  const awaiting = !exited && cur === 'qualified' && pendingConversion;
  const nexts = exited || awaiting ? [] : NEXT_STEPS[cur] ?? [];
  const exitStyle = getStatusStyle(cur);

  const stateOf = (st: string): NodeState => {
    if (exited) return 'upcoming';
    if (st === cur) return 'current';
    if (awaiting && st === 'converted') return 'pending';
    if (nexts.includes(st)) return 'next';
    if (RANK[st] < curRank) return 'done';
    return 'upcoming';
  };

  // edge i links rank i → rank i+1: 0 new→contacted · 1 fork · 2 merge · 3 qualified→converted
  const edge = (reachedRank: number, fromStages: string[]): EdgeState => {
    if (exited) return 'idle';
    if (curRank >= reachedRank) return 'done';
    if (reachedRank === 4 && awaiting) return 'pending';
    if (fromStages.includes(cur)) return 'next';
    return 'idle';
  };

  const prevRank = preview ? RANK[preview] : undefined;
  const blinkEdge = (i: number) => {
    if (exited || prevRank === undefined || preview === cur) return false;
    const lo = Math.min(curRank, prevRank);
    const hi = Math.max(curRank, prevRank);
    return i >= lo && i < hi;
  };
  const exitPreview = preview && RANK[preview] === undefined && preview !== cur ? preview : null;

  const node = (st: string) => <PipelineNode key={st} st={st} state={stateOf(st)} dim={exited} preview={preview === st} onPick={onPick} />;

  const pill = (st: string, ghost: boolean) => {
    const sty = getStatusStyle(st);
    return (
      <span
        className={ghost ? 'ldv-edge-blink' : undefined}
        style={{
          marginLeft: '8px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '2.8px 9px',
          borderRadius: '20px',
          fontSize: '0.64rem',
          fontWeight: 700,
          whiteSpace: 'nowrap',
          background: sty.bg,
          color: sty.color,
          border: `1px ${ghost ? 'dashed' : 'solid'} ${sty.border}`,
        }}
      >
        {STATUS_EMOJI[st]} {ghost ? 'Move to' : 'Exited'}: {statusLabel(st)}
      </span>
    );
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0, overflowX: 'auto', paddingBottom: '2px' }}>
      {node('new')}
      <Line e={edge(1, ['new'])} dim={exited} blink={blinkEdge(0)} />
      {node('contacted')}
      <Branch kind="fork" e={edge(2, ['contacted'])} dim={exited} blink={blinkEdge(1)} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: `${STACK_GAP}px` }}>
        {node('interested')}
        {node('follow_up')}
      </div>
      <Branch kind="merge" e={edge(3, ['interested', 'follow_up'])} dim={exited} blink={blinkEdge(2)} />
      {node('qualified')}
      <Line e={edge(4, ['qualified'])} dim={exited} blink={blinkEdge(3)} />
      {node('converted')}
      {exited && pill(cur, false)}
      {exitPreview && pill(exitPreview, true)}
    </div>
  );
};

const MoveMenu: React.FC<{
  current: string | null;
  canConvert: boolean;
  onPick: (status: string) => void;
  onPreview: (status: string | null) => void;
}> = ({ current, canConvert, onPick, onPreview }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const cur = toValidStatus(current);

  useEffect(() => {
    if (!open) {
      onPreview(null);
      return;
    }
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const item = (st: string) => {
    const s = getStatusStyle(st);
    return (
      <button
        key={st}
        onClick={() => {
          setOpen(false);
          onPick(st);
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = G.shellBg;
          onPreview(st);
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          onPreview(null);
        }}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '6.5px 10px', background: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer', textAlign: 'left', fontSize: '0.72rem', fontWeight: 600, color: '#1f2937' }}
      >
        <span style={{ width: '18px', textAlign: 'center' }}>{STATUS_EMOJI[st]}</span>
        <span style={{ flex: 1 }}>{statusLabel(st)}</span>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, opacity: 0.7 }} />
      </button>
    );
  };

  const groupLabel = (t: string) => (
    <div style={{ padding: '6px 10px 3px', fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase', color: C.muted }}>{t}</div>
  );

  // A worked lead can't go back to "new"; staff can't pick "converted".
  const stages = MAIN_FLOW.filter((st) => st !== cur && st !== 'new' && (st !== 'converted' || canConvert));

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '4.6px', padding: '6.5px 11px', background: 'white', border: `1px solid ${G.line}`, borderRadius: '7.4px', color: C.teal, fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer' }}
      >
        MOVE TO <ChevronDown size={12} />
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', width: '210px', background: 'white', border: `1px solid ${G.line}`, borderRadius: '10px', boxShadow: '0 12px 28px rgba(17,85,80,0.18)', padding: '4px', zIndex: 50 }}>
          {groupLabel('Stage')}
          {stages.map(item)}
          <div style={{ height: 1, background: G.soft, margin: '4px 6px' }} />
          {groupLabel('Exit')}
          {EXIT_STATUSES.filter((st) => st !== cur).map(item)}
        </div>
      )}
    </div>
  );
};

const LeadDetailView: React.FC = () => {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [noteDate, setNoteDate] = useState<Date | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCallLog, setShowCallLog] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [statusTarget, setStatusTarget] = useState<string | null>(null);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | null>(null);
  const [canConvert, setCanConvert] = useState(false); // admins only — comes from the API
  const [previewTo, setPreviewTo] = useState<string | null>(null); // hovered "move to" target

  const fetchLead = async () => {
    try {
      setLoading(true); setError(null);
      const res = await apiClient.get(`/v1/leads/${leadId}`);
      if (res.data.success) { setLead(withNotes(res.data.data)); setCanConvert(!!res.data.viewer?.can_convert); }
      else setError(res.data.error || 'Failed to load lead');
    } catch (e: any) { setError(e.response?.data?.error || 'Failed to load lead'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (leadId) fetchLead(); }, [leadId]);

  const handleCall = () => {
    if (!lead?.phone) return;
    window.open(`tel:${lead.phone.replace(/[\s\-\(\)]/g, '')}`);
    setShowCallLog(true);
  };

  const saveNote = async (text: string, date: Date | null) => {
    try {
      const form = new FormData();
      form.append('type', 'note'); form.append('note', text);
      if (date) form.append('event_date', date.toISOString().split('T')[0]);
      const res = await apiClient.post(`/v1/leads/${leadId}/notes`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) { setLead(withNotes(res.data.data)); toast.current?.show({ severity: 'success', summary: 'Note Saved', life: 3000 }); }
    } catch (e: any) { toast.current?.show({ severity: 'error', summary: 'Error', detail: e.response?.data?.error || 'Failed', life: 3000 }); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSubmitting(true);
    await saveNote(noteText.trim(), noteDate);
    setNoteText(''); setNoteDate(null); setSubmitting(false);
  };

  // Status change = a note carrying a status override, so it shows up in the
  // timeline and the backend applies the status in one call.
  const handleStatusChange = async (to: string, note: string): Promise<boolean> => {
    try {
      // Staff can't convert: they send a request that an admin verifies.
      if (to === 'converted' && !canConvert) {
        const res = await apiClient.post(`/v1/leads/${leadId}/request-conversion`, { note: note.trim() });
        if (res.data.success) {
          setLead(withNotes(res.data.data));
          toast.current?.show({ severity: 'success', summary: 'Request sent', detail: 'Waiting for admin approval', life: 3000 });
          return true;
        }
        toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.error || 'Failed to send request', life: 3500 });
        return false;
      }

      const text = `🔄 Status changed: ${statusLabel(lead?.status)} → ${statusLabel(to)}${note.trim() ? `\n${note.trim()}` : ''}`;
      const form = new FormData();
      form.append('type', 'note');
      form.append('note', text);
      form.append('status', to);
      const res = await apiClient.post(`/v1/leads/${leadId}/notes`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        setLead(withNotes(res.data.data));
        toast.current?.show({
          severity: 'success',
          summary: 'Status updated',
          detail: to === 'converted' ? 'Converted — auto-approved' : `Moved to ${statusLabel(to)}`,
          life: 2500,
        });
        return true;
      }
      toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.error || 'Failed to update status', life: 3500 });
    } catch (e: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: e.response?.data?.error || 'Failed to update status', life: 3500 });
    }
    return false;
  };

  // Admin: approve / reject a conversion request from staff
  const handleReview = async (action: 'approve' | 'reject', note: string): Promise<boolean> => {
    try {
      const res = await apiClient.post(`/v1/leads/${leadId}/review-conversion`, { action, note: note.trim() });
      if (res.data.success) {
        setLead(withNotes(res.data.data));
        toast.current?.show({ severity: 'success', summary: action === 'approve' ? 'Conversion approved' : 'Conversion rejected', life: 2500 });
        return true;
      }
      toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.error || 'Failed to review', life: 3500 });
    } catch (e: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: e.response?.data?.error || 'Failed to review', life: 3500 });
    }
    return false;
  };

  const handleLeadSaved = (updatedLead: SavedLead) => {
    setLead((prev) =>
      prev
        ? {
            ...prev,
            name: updatedLead.name ?? prev.name,
            phone: updatedLead.phone ?? prev.phone,
            email: updatedLead.email ?? prev.email,
            source: updatedLead.source ?? prev.source,
            status: updatedLead.status ?? prev.status,
            notes: updatedLead.notes ?? prev.notes,
            city: updatedLead.city ?? prev.city,
            interested_in: updatedLead.interested_in ?? prev.interested_in,
          }
        : null
    );
    toast.current?.show({ severity: 'success', summary: 'Lead Updated', detail: `${updatedLead.name} has been saved.`, life: 2500 });
  };

  const handleCallLogSaved = (updatedLead: CallLogLead) => {
    setLead(withNotes(updatedLead));
  };

  if (loading && !lead)
    return (
      <div style={SHELL}>
        <div style={{ ...INNER, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '340px' }}>
          <div style={{ textAlign: 'center' }}>
            <ProgressSpinner style={{ width: '37px', height: '37px' }} strokeWidth="3" animationDuration=".8s" />
            <p style={{ marginTop: '9.2px', color: C.teal, fontSize: '0.81rem', fontWeight: 500 }}>Loading lead…</p>
          </div>
        </div>
      </div>
    );

  if (error || !lead)
    return (
      <div style={SHELL}>
        <div style={{ ...INNER, padding: '22px' }}>
          <Message severity="error" text={error || 'Lead not found'} style={{ marginBottom: '16.6px', fontSize: '0.83rem', width: '100%' }} />
          <button
            onClick={() => navigate('/leads')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4.6px', padding: '6.5px 13.8px', background: C.teal, border: 'none', borderRadius: '7.4px', color: 'white', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}
          >
            <ArrowLeft size={12} /> Back to Leads
          </button>
        </div>
      </div>
    );

  const bs = getBadgeStyle(lead.status);
  const initials = getInitials(lead.name);
  const avatarColor = avc(lead.name || lead.email);
  const reversedNotes = [...lead.notes_list].reverse();
  const hasMoreNotes = reversedNotes.length > 3;
  const visibleNotes = notesExpanded ? reversedNotes : reversedNotes.slice(0, 3);
  const SourceIcon = SOURCE_ICONS[(lead.source || '').toLowerCase()] || Tag;

  const events: TimelineEvent[] = [
    { kind: 'created', date: lead.created_at ?? '' } as const,
    ...lead.notes_list.map(
      (n): TimelineEvent =>
        n.type === 'call_log'
          ? { kind: 'call_log', date: n.event_date ?? n.created_at ?? '', note: n }
          : { kind: 'note', date: n.event_date ?? n.created_at ?? '', note: n }
    ),
  ].sort((a, b) => (a.date > b.date ? -1 : 1));

  return (
    <div style={{ margin: '0 auto' }}>
      <style>{`
        .ldv-act:hover { transform: translateY(-1px); filter: brightness(1.06); }
        .ldv-spin { animation: ldv-spin 0.9s linear infinite; }
        .ldv-node-current { animation: ldvRing 1.6s ease-out infinite; }
        .ldv-node-dot { animation: ldvBlink 1s ease-in-out infinite; }
        .ldv-node-next { animation: ldvNext 1.3s ease-in-out infinite; }
        .ldv-live-dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; animation: ldvBlink 1s ease-in-out infinite; }
        @keyframes ldvRing { 0% { box-shadow: 0 0 0 0 rgba(200,233,75,0.9); } 70% { box-shadow: 0 0 0 9px rgba(200,233,75,0); } 100% { box-shadow: 0 0 0 0 rgba(200,233,75,0); } }
        @keyframes ldvBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0.2; } }
        @keyframes ldvNext { 0%, 100% { box-shadow: 0 0 0 0 rgba(200,233,75,0); } 50% { box-shadow: 0 0 0 4px rgba(200,233,75,0.55); } }
        .ldv-node-pending { animation: ldvPending 1.8s ease-in-out infinite; }
        .ldv-node-preview { animation: ldvFast 0.35s ease-in-out infinite !important; }
        .ldv-edge-blink { animation: ldvEdgeFast 0.35s ease-in-out infinite; }
        @keyframes ldvPending { 0%, 100% { box-shadow: 0 0 0 0 rgba(217,119,6,0); } 50% { box-shadow: 0 0 0 4px rgba(217,119,6,0.35); } }
        @keyframes ldvFast { 0%, 100% { background: #fff; transform: scale(1); box-shadow: 0 0 0 0 rgba(200,233,75,0); } 50% { background: #c8e94b; transform: scale(1.3); box-shadow: 0 0 0 5px rgba(200,233,75,0.55); } }
        @keyframes ldvEdgeFast { 0%, 100% { opacity: 0.2; } 50% { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { .ldv-node-current, .ldv-node-dot, .ldv-node-next, .ldv-node-pending, .ldv-node-preview, .ldv-edge-blink, .ldv-live-dot { animation: none !important; } }
        @keyframes ldv-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
      <Toast ref={toast} />

      <AddEditLeadModal
        visible={showEditModal}
        onHide={() => setShowEditModal(false)}
        onSaved={handleLeadSaved}
        leadId={lead.id}
      />

      <LogCallModal
        visible={showCallLog}
        onHide={() => setShowCallLog(false)}
        leadId={lead.id}
        leadName={lead.name}
        onSaved={handleCallLogSaved}
      />

      <StatusChangeModal
        visible={!!statusTarget}
        onHide={() => setStatusTarget(null)}
        leadName={lead.name}
        from={lead.status}
        to={statusTarget}
        mode={statusTarget === 'converted' && !canConvert ? 'request' : 'status'}
        onConfirm={(note) => handleStatusChange(statusTarget as string, note)}
      />

      <StatusChangeModal
        visible={!!reviewAction}
        onHide={() => setReviewAction(null)}
        leadName={lead.name}
        from={lead.status}
        to={reviewAction === 'approve' ? 'converted' : reviewAction ? lead.status : null}
        mode={reviewAction ?? 'status'}
        onConfirm={(note) => handleReview(reviewAction as 'approve' | 'reject', note)}
      />

      {/* ── HEADER CARD ─────────────────────────────────────────────────── */}
      <div style={{ ...SHELL, marginBottom: '11px' }}>
        <div style={{ ...INNER, padding: '13.8px 16.6px', position: 'relative' }}>
          {/* glow is clipped in its own layer so the Move-to menu can overflow the card */}
          <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: '9.2px', pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '140px', height: '140px', background: 'rgba(200,233,75,0.12)', borderRadius: '50%', filter: 'blur(35px)' }} />
          </div>

          {/* Row 1 */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '11px', position: 'relative', zIndex: 1, flexWrap: 'wrap', gap: '7.4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px', flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate('/leads')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '3.7px', padding: '3.7px 9.2px', background: 'rgba(17,85,80,0.08)', border: '1px solid rgba(17,85,80,0.18)', borderRadius: '20px', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.7px', textTransform: 'uppercase', color: C.teal, cursor: 'pointer' }}
              >
                <Users size={10} /> Leads
              </button>
              <ChevronRight size={10} color={C.muted} />
              <h1 style={{ margin: 0, fontSize: '1.01rem', fontWeight: 700, color: C.teal, letterSpacing: '-0.2px' }}>{lead.name}</h1>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '2.8px 8.3px', borderRadius: '20px', fontSize: '0.63rem', fontWeight: 700, textTransform: 'capitalize', background: bs.bg, color: bs.color, border: `1px solid ${bs.border}` }}><span className="ldv-live-dot" style={{ background: bs.color }} />{statusLabel(lead.status)}</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6.5px' }}>
              <button
                onClick={() => setShowEditModal(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '4.6px', padding: '6.5px 13px', background: C.limeDim, border: `1px solid ${C.limeBorder}`, borderRadius: '7.4px', color: '#4a5e00', fontSize: '0.71rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(200,233,75,0.3)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = C.limeDim; }}
              >
                <Pencil size={12} /> EDIT
              </button>

              <button
                onClick={handleCall}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '4.6px', padding: '6.5px 14px', background: 'rgb(17,85,80)', border: 'none', borderRadius: '7.4px', color: 'white', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(17,85,80,0.3)', transition: 'all 0.2s' }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.background = 'rgb(22,112,106)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.background = 'rgb(17,85,80)'; }}
              >
                <Phone size={12} /> CALL
              </button>
            </div>
          </div>

          {/* Row 2: meta strip */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '13px', position: 'relative', zIndex: 1, flexWrap: 'wrap' }}>
            <div style={{ width: '35px', height: '35px', borderRadius: '50%', background: avatarColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.78rem', fontWeight: 700, flexShrink: 0, boxShadow: `0 2px 8px ${avatarColor}55` }}>
              {initials}
            </div>
            <div style={{ display: 'flex', gap: '9.2px', flexWrap: 'wrap', flex: 1 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3.7px', fontSize: '0.72rem', color: C.sub }}>
                <Phone size={11} color={C.muted} /> {lead.phone || '—'}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3.7px', fontSize: '0.72rem', color: C.sub }}>
                <Mail size={11} color={C.muted} /> {lead.email || '—'}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3.7px', fontSize: '0.72rem', color: C.sub }}>
                <MapPin size={11} color={C.muted} /> {lead.city || '—'}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3.7px', fontSize: '0.72rem', color: C.sub, textTransform: 'capitalize' }}>
                <SourceIcon size={11} color={C.muted} /> {lead.source?.replace('_', ' ') || '—'}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3.7px', fontSize: '0.72rem', color: C.sub }}>
                <span style={{ display: 'inline-flex' }}><Users size={11} color={C.muted} /></span> Added {fmtDate(lead.created_at)}
              </span>
              {(() => {
                const fu = followUpInfo(lead.follow_up_at);
                return fu ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '1.8px 8px', borderRadius: '20px', background: fu.bg, color: fu.color, fontSize: '0.68rem', fontWeight: 700 }}>
                    ⏰ Follow up: {fu.text}
                  </span>
                ) : null;
              })()}
            </div>
            <span style={{ padding: '1.8px 7.4px', borderRadius: '20px', background: 'rgba(17,85,80,0.07)', color: C.tealMid, fontSize: '0.66rem', fontWeight: 600 }}>
              {lead.notes_list.length} notes
            </span>
          </div>

          {/* Row 3: pipeline + next step / approval */}
          {(() => {
            const cur = toValidStatus(lead.status);
            const exited = RANK[cur] === undefined;
            const pending = lead.conversion_request?.status === 'pending';
            const rejected = lead.conversion_request?.status === 'rejected';
            const locked = cur === 'converted' && !canConvert; // staff can't touch converted leads
            const nexts = pending ? [] : NEXT_STEPS[cur] ?? [];
            const primary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6.5px 13px', background: C.lime, border: 'none', borderRadius: '7.4px', color: C.teal, fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.2px', cursor: 'pointer', boxShadow: '0 2px 10px rgba(200,233,75,0.45)', transition: 'all 0.2s', whiteSpace: 'nowrap' };
            const secondary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6.5px 13px', background: C.limeDim, border: `1px solid ${C.limeBorder}`, borderRadius: '7.4px', color: '#4a5e00', fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.2px', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' };
            const pillSty = (bg: string, color: string, border: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 11px', borderRadius: '20px', background: bg, color, border: `1px solid ${border}`, fontSize: '0.66rem', fontWeight: 700, whiteSpace: 'nowrap' });
            const btnLabel = (n: string) => (n === 'converted' ? (canConvert ? 'MARK CONVERTED' : 'REQUEST CONVERSION') : `MARK ${statusLabel(n).toUpperCase()}`);
            return (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '11px', flexWrap: 'wrap', marginTop: '11px', paddingTop: '11px', borderTop: `1px solid ${G.soft}`, position: 'relative', zIndex: 2 }}>
                <StatusPipeline status={lead.status} pendingConversion={pending} preview={previewTo} onPick={locked ? undefined : (st) => setStatusTarget(st)} />
                <div style={{ display: 'flex', alignItems: 'center', gap: '6.5px', flexWrap: 'wrap' }}>
                  {!locked &&
                    nexts.map((n, i) => (
                      <button
                        key={n}
                        onClick={() => setStatusTarget(n)}
                        onMouseEnter={() => setPreviewTo(n)}
                        onMouseLeave={() => setPreviewTo(null)}
                        className="ldv-act"
                        style={i === 0 ? primary : secondary}
                      >
                        {btnLabel(n)} {i === 0 && <ArrowRight size={12} />}
                      </button>
                    ))}

                  {/* staff asked for a conversion */}
                  {pending && !canConvert && <span style={pillSty('rgba(245,158,11,0.12)', '#b45309', 'rgba(245,158,11,0.35)')}>⏳ Awaiting admin approval</span>}
                  {/* admin reviews it */}
                  {pending && canConvert && (
                    <>
                      <span style={pillSty('rgba(245,158,11,0.12)', '#b45309', 'rgba(245,158,11,0.35)')}>⏳ Conversion requested</span>
                      <button onClick={() => setReviewAction('approve')} className="ldv-act" style={primary}>
                        <Check size={12} strokeWidth={3} /> APPROVE
                      </button>
                      <button onClick={() => setReviewAction('reject')} className="ldv-act" style={{ ...secondary, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#c62828' }}>
                        <X size={12} /> REJECT
                      </button>
                    </>
                  )}
                  {rejected && cur === 'qualified' && !pending && (
                    <span title={lead.conversion_request?.review_note || ''} style={pillSty('rgba(239,68,68,0.08)', '#c62828', 'rgba(239,68,68,0.3)')}>
                      ❌ Conversion rejected{lead.conversion_request?.review_note ? ` — ${lead.conversion_request.review_note}` : ''}
                    </span>
                  )}

                  {exited && !locked && (
                    <button onClick={() => setStatusTarget('contacted')} onMouseEnter={() => setPreviewTo('contacted')} onMouseLeave={() => setPreviewTo(null)} className="ldv-act" style={secondary}>
                      <RotateCcw size={12} /> REOPEN
                    </button>
                  )}
                  {!locked && <MoveMenu current={lead.status} canConvert={canConvert} onPick={(st) => setStatusTarget(st)} onPreview={setPreviewTo} />}
                  {locked && <span style={pillSty('rgba(22,163,74,0.1)', '#15803d', 'rgba(22,163,74,0.3)')}>🔒 Converted — only an admin can change this</span>}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ── MAIN GRID ────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '11px' }}>
        {/* LEFT COLUMN */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
          <Card>
            <SectionHeader icon={IdCard} title="Contact Info" />
            <InfoRow icon={Phone} label="Phone" value={lead.phone} />
            <InfoRow icon={Mail} label="Email" value={lead.email} />
            <InfoRow icon={MapPin} label="City" value={lead.city} />
            <InfoRow icon={SourceIcon} label="Source" value={lead.source?.replace('_', ' ') || null} />
            <InfoRow icon={Tag} label="Interest" value={lead.interested_in} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px', padding: '8.3px 0' }}>
              <div style={{ width: '26px', height: '26px', background: C.limeDim, borderRadius: '6.5px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Tag size={12} color={C.teal} />
              </div>
              <span style={{ fontSize: '0.67rem', color: C.muted, fontWeight: 600, width: '65px', flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Status</span>
              <span style={{ padding: '2.8px 8.3px', borderRadius: '20px', fontSize: '0.63rem', fontWeight: 700, textTransform: 'capitalize', background: bs.bg, color: bs.color, border: `1px solid ${bs.border}` }}>{statusLabel(lead.status)}</span>
            </div>
          </Card>

          {lead.notes && (
            <Card>
              <SectionHeader icon={AlignLeft} title="Original Note" />
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#1f2937', lineHeight: 1.65, background: G.shellBg, padding: '10.1px 12px', borderRadius: '8.3px', border: `1px solid ${G.line}`, borderLeft: `3px solid ${C.lime}` }}>
                {lead.notes}
              </p>
            </Card>
          )}

          <Card>
            <SectionHeader
              icon={MessageSquare} title="Activity Notes" count={lead.notes_list.length}
              right={
                <button
                  onClick={() => setShowTimeline(true)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '3.7px', padding: '4.6px 10.1px', background: C.limeDim, border: `1px solid ${C.limeBorder}`, borderRadius: '6.5px', color: '#4a5e00', fontSize: '0.66rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(200,233,75,0.3)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = C.limeDim; }}
                >
                  <Clock size={11} /> TIMELINE
                </button>
              }
            />
            {lead.notes_list.length === 0 ? (
              <div style={{ padding: '18.5px', textAlign: 'center', color: C.muted, fontSize: '0.77rem', background: G.shellBg, borderRadius: '8.3px', border: `1px dashed ${G.line}` }}>
                <MessageSquare size={20} style={{ display: 'block', margin: '0 auto 5.5px', opacity: 0.3 }} />
                No notes yet
              </div>
            ) : (
              <>
                {visibleNotes.map((note, i) => <NoteItem key={i} note={note} />)}
                {hasMoreNotes && (
                  <button
                    onClick={() => setNotesExpanded(!notesExpanded)}
                    style={{ width: '100%', padding: '6.5px', background: 'transparent', border: `1px solid ${G.line}`, borderRadius: '7.4px', color: C.teal, fontSize: '0.71rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3.7px', transition: 'all 0.2s', marginTop: '3.7px' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(17,85,80,0.04)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {notesExpanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                    {notesExpanded ? 'Show less' : `Show ${lead.notes_list.length - 3} more notes`}
                  </button>
                )}
              </>
            )}
          </Card>

          <Card>
            <SectionHeader icon={PlusCircle} title="Add a Note" />
            <div style={{ marginBottom: '10.1px' }}>
              <GlobalDateField
                label="Note Date (optional)"
                value={noteDate}
                onChange={(v) => setNoteDate((v as Date | null) ?? null)}
                placeholder="dd/mm/yyyy"
                disabled={submitting}
                showButtonBar
              />
            </div>
            <div style={{ marginBottom: '10.1px' }}>
              <GlobalTextArea
                label="Note"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Write a note…"
                rows={3}
                disabled={submitting}
                autoResize
              />
            </div>
            <button
              onClick={handleAddNote} disabled={!noteText.trim() || submitting}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '4.6px', padding: '6.5px 14.8px', background: noteText.trim() ? C.lime : 'rgba(200,233,75,0.3)', border: 'none', borderRadius: '7.4px', color: C.teal, fontSize: '0.72rem', fontWeight: 700, cursor: noteText.trim() ? 'pointer' : 'not-allowed', transition: 'all 0.2s', boxShadow: noteText.trim() ? '0 2px 7px rgba(200,233,75,0.3)' : 'none' }}
              onMouseEnter={(e) => { if (noteText.trim()) e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              {submitting ? (
                <><Loader2 size={12} className="ldv-spin" /> SAVING…</>
              ) : (
                <><Save size={12} /> SAVE NOTE</>
              )}
            </button>
          </Card>
        </div>

        {/* RIGHT COLUMN */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
          <QuickShareCard lead={lead} />

          <Card>
            <SectionHeader icon={Zap} title="Quick Actions" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6.5px' }}>
              {[
                { label: 'Call', icon: Phone, bg: C.teal, col: '#fff', shadow: '0 2px 8px rgba(17,85,80,0.3)', brd: 'transparent', fn: handleCall },
                { label: 'WhatsApp', icon: MessageCircle, bg: '#25D366', col: '#fff', shadow: '0 2px 8px rgba(37,211,102,0.3)', brd: 'transparent', fn: () => window.open(`https://wa.me/${lead.phone?.replace(/[\s\-\(\)]/g, '')}`) },
                { label: 'Send Email', icon: Mail, bg: C.teal, col: '#fff', shadow: '0 2px 8px rgba(17,85,80,0.3)', brd: 'transparent', fn: () => window.open(`mailto:${lead.email}`) },
                { label: 'Log Call', icon: List, bg: C.limeDim, col: '#4a5e00', shadow: 'none', brd: C.limeBorder, fn: () => setShowCallLog(true) },
                { label: 'Edit Lead', icon: Pencil, bg: C.limeDim, col: '#4a5e00', shadow: 'none', brd: C.limeBorder, fn: () => setShowEditModal(true) },
                { label: 'View Timeline', icon: Clock, bg: 'rgba(17,85,80,0.06)', col: C.teal, shadow: 'none', brd: 'rgba(17,85,80,0.14)', fn: () => setShowTimeline(true) },
              ].map((btn) => (
                <button
                  key={btn.label} onClick={btn.fn} className="ldv-act"
                  style={{ width: '100%', padding: '8.3px 13px', background: btn.bg, border: `1px solid ${btn.brd}`, borderRadius: '8.3px', color: btn.col, fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6.5px', transition: 'all 0.18s', boxShadow: btn.shadow }}
                >
                  <btn.icon size={13} /> {btn.label}
                </button>
              ))}
            </div>
          </Card>

          <Card>
            <SectionHeader icon={User} title="Assignment" />
            {lead.assigned_to ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10.1px', padding: '10.1px 12px', background: C.limeDim, borderRadius: '9.2px', border: `1px solid ${C.limeBorder}`, marginBottom: '9.2px' }}>
                  <div style={{ width: '35px', height: '35px', borderRadius: '50%', background: avc(lead.assigned_to.email), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.72rem', fontWeight: 700, flexShrink: 0 }}>
                    {ini2(lead.assigned_to.email)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 600, color: C.teal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.assigned_to.email}</div>
                    {lead.assigned_to.role && (
                      <span style={{ display: 'inline-block', marginTop: '2.8px', padding: '1.8px 6.5px', borderRadius: '20px', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', background: 'rgba(200,233,75,0.3)', color: '#4a5e00', border: `1px solid ${C.limeBorder}` }}>
                        {lead.assigned_to.role}
                      </span>
                    )}
                  </div>
                </div>
                {lead.assigned_by && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4.6px', fontSize: '0.69rem', color: C.muted }}>
                    <Send size={11} />
                    By <span style={{ color: C.sub, fontWeight: 600, marginLeft: '1.8px' }}>{lead.assigned_by.email.split('@')[0]}</span>
                    {lead.assigned_at && <span style={{ marginLeft: 'auto' }}>{fmtDate(lead.assigned_at)}</span>}
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: '14.8px', textAlign: 'center', color: C.muted, fontSize: '0.77rem', background: G.shellBg, borderRadius: '8.3px', border: `1px dashed ${G.line}` }}>
                <UserPlus size={18} style={{ display: 'block', margin: '0 auto 5.5px', opacity: 0.35 }} />
                Not assigned yet
              </div>
            )}
          </Card>

          <Card>
            <SectionHeader icon={BarChart3} title="Activity" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6.5px' }}>
              {[
                { label: 'Total Notes', val: lead.notes_list.length, icon: MessageSquare },
                { label: 'Call Logs', val: lead.notes_list.filter((n) => n.type === 'call_log').length, icon: Phone },
                { label: 'Last Activity', val: fmtDate(reversedNotes[0]?.created_at || lead.created_at), icon: Clock },
              ].map((s) => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '8.3px', padding: '7.4px 9.2px', background: G.shellBg, borderRadius: '7.4px', border: `1px solid ${G.line}` }}>
                  <div style={{ width: '24px', height: '24px', background: C.limeDim, borderRadius: '5.5px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <s.icon size={12} color="#4a5e00" />
                  </div>
                  <span style={{ flex: 1, fontSize: '0.71rem', color: C.sub }}>{s.label}</span>
                  <span style={{ fontSize: '0.76rem', fontWeight: 700, color: C.teal }}>{s.val}</span>
                </div>
              ))}
            </div>
          </Card>

          {lead.source && (
            <Card>
              <SectionHeader icon={Tag} title="Source" />
              <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px', padding: '9.2px 11px', background: C.limeDim, border: `1px solid ${C.limeBorder}`, borderRadius: '8.3px' }}>
                <SourceIcon size={17} color="#4a5e00" />
                <span style={{ fontSize: '0.84rem', fontWeight: 700, color: '#4a5e00', textTransform: 'capitalize' }}>{lead.source.replace('_', ' ')}</span>
              </div>
            </Card>
          )}

          {/* Campaign / form / sheet / extra fields — hidden when the lead has none */}
          <LeadOriginCard lead={lead} />
        </div>
      </div>

      {/* ── TIMELINE SIDEBAR ─────────────────────────────────────────────── */}
      <LeadTimelineSidebar
        visible={showTimeline}
        onHide={() => setShowTimeline(false)}
        lead={lead}
        events={events}
      />
    </div>
  );
};

export default LeadDetailView;